import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import { usePermissions } from 'hooks/Permission/usePermission';
import { useNotificationHub } from 'hooks/useNotificationHub';
import { useMarkAllNotificationsRead, useMarkNotificationRead, useNotifications } from 'hooks/useNotifications';
import { permissions } from 'utils/permissions';

import { IconBell, IconBellOff, IconCheck } from '@tabler/icons-react';

const MAX_VISIBLE_NOTIFICATIONS = 8;

function formatNotificationDate(value: string) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('hu-HU', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
}

export default function NotificationSection() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasPermission, loadState } = usePermissions();
  const canListNotifications = hasPermission(permissions.notification.NOTIFICATION_LIST);
  const canReadNotifications = hasPermission(permissions.notification.NOTIFICATION_READ);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const notificationsEnabled = loadState === 'loaded' && canListNotifications;
  const notificationsQuery = useNotifications({ pageNumber: 1, pageSize: 20 }, notificationsEnabled);
  useNotificationHub(notificationsEnabled);
  const markNotificationRead = useMarkNotificationRead();
  const markAllNotificationsRead = useMarkAllNotificationsRead();

  const notifications = useMemo(() => notificationsQuery.data?.page ?? [], [notificationsQuery.data?.page]);
  const visibleNotifications = notifications.slice(0, MAX_VISIBLE_NOTIFICATIONS);
  const unreadCount = notifications.filter((notification) => !notification.megtekintett).length;

  useEffect(() => {
    if (open && canListNotifications) {
      notificationsQuery.refetch();
    }
    // refetch is intentionally omitted to avoid reopening-trigger loops from react-query identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canListNotifications]);

  if (loadState !== 'loaded' || !canListNotifications) {
    return null;
  }

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current?.contains(event.target as Node)) {
      return;
    }

    setOpen(false);
  };

  const handleNotificationClick = (notificationId: string, alreadyRead: boolean, navigationUrl?: string | null) => {
    if (canReadNotifications && !alreadyRead) {
      markNotificationRead.mutate(notificationId);
    }

    if (navigationUrl) {
      setOpen(false);
      navigate(navigationUrl);
    }
  };

  const handleMarkAllRead = () => {
    if (!canReadNotifications || unreadCount === 0) {
      return;
    }

    markAllNotificationsRead.mutate();
  };

  return (
    <>
      <Box ref={anchorRef} aria-controls={open ? 'notifications-menu' : undefined} aria-haspopup="true" onClick={handleToggle}>
        <Badge badgeContent={unreadCount} color="error" max={99} overlap="circular" invisible={unreadCount === 0}>
          <Avatar
            variant="rounded"
            sx={{
              ...theme.typography.commonAvatar,
              ...theme.typography.mediumAvatar,
              overflow: 'hidden',
              transition: 'all .2s ease-in-out',
              color: 'text.secondary',
              background: 'transparent',
              cursor: 'pointer',
              '&:hover': {
                color: theme.vars.palette.primary.main,
                background: theme.vars.palette.action.hover
              },
              ...theme.applyStyles('dark', {
                color: theme.vars.palette.secondary.light,
                background: 'transparent',
                '&:hover': {
                  color: theme.vars.palette.secondary.light,
                  background: theme.vars.palette.dark.main
                }
              })
            }}
            aria-label="értesítések"
          >
            <IconBell stroke={1.5} size="20px" />
          </Avatar>
        </Badge>
      </Box>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[{ name: 'offset', options: { offset: [0, 14] } }]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions in={open} {...TransitionProps}>
              <Paper>
                {open && (
                  <MainCard border={false} elevation={8} content={false} boxShadow shadow={theme.shadows[8]}>
                    <Box sx={{ width: { xs: 320, sm: 420 }, maxWidth: 'calc(100vw - 32px)' }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Értesítések
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {unreadCount > 0 ? `${unreadCount} olvasatlan` : 'Nincs olvasatlan értesítés'}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          startIcon={<IconCheck size="16px" />}
                          disabled={!canReadNotifications || unreadCount === 0 || markAllNotificationsRead.isPending}
                          onClick={handleMarkAllRead}
                        >
                          Összes olvasott
                        </Button>
                      </Stack>
                      <Divider />

                      {notificationsQuery.isLoading ? (
                        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 180 }}>
                          <CircularProgress size={24} />
                        </Stack>
                      ) : visibleNotifications.length === 0 ? (
                        <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ minHeight: 180, px: 3 }}>
                          <IconBellOff stroke={1.5} size="32px" color={theme.palette.text.secondary} />
                          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                            Jelenleg nincs megjeleníthető értesítés.
                          </Typography>
                        </Stack>
                      ) : (
                        <List sx={{ maxHeight: 440, overflowY: 'auto', p: 0 }}>
                          {visibleNotifications.map((notification, index) => (
                            <Box key={notification.id}>
                              <ListItemButton
                                alignItems="flex-start"
                                onClick={() => handleNotificationClick(notification.id, notification.megtekintett, notification.navigaciosUrl)}
                                sx={{
                                  px: 2,
                                  py: 1.25,
                                  bgcolor: notification.megtekintett ? 'background.paper' : 'action.hover',
                                  borderLeft: notification.megtekintett ? '3px solid transparent' : `3px solid ${theme.palette.primary.main}`
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                      <Typography variant="body2" sx={{ fontWeight: notification.megtekintett ? 500 : 700 }}>
                                        {notification.targy}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                        {formatNotificationDate(notification.letrehozasIdeje)}
                                      </Typography>
                                    </Stack>
                                  }
                                  secondary={
                                    notification.szoveg ? (
                                      <Typography
                                        component="span"
                                        variant="caption"
                                        sx={{
                                          color: 'text.secondary',
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                          mt: 0.5
                                        }}
                                      >
                                        {notification.szoveg}
                                      </Typography>
                                    ) : null
                                  }
                                />
                              </ListItemButton>
                              {index < visibleNotifications.length - 1 && <Divider />}
                            </Box>
                          ))}
                        </List>
                      )}
                    </Box>
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
